import React, { useEffect, useMemo, useState } from "react";
import {
  HiOutlineArrowPath,
  HiOutlineDocumentText,
  HiOutlinePaperClip,
  HiOutlineTrash,
  HiOutlineXMark,
} from "react-icons/hi2";
import DashboardLayout from "../../../components/dashboard/DashboardLayout";
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  LoadingState,
} from "../../../components/dashboard/DashboardStates";
import { useToast } from "../../../context/ToastContext";
import * as casesApi from "../../../api/casesApi";
import { getApiErrorMessage } from "../../../utils/apiResponse";
import { resolveMediaUrl } from "../../../utils/mediaUrl";

const STATUS_OPTIONS = [
  {
    value: "Received",
    label: "Received",
    tone: "slate",
    description: "Logged and awaiting assignment",
  },
  {
    value: "InReview",
    label: "In review",
    tone: "amber",
    description: "Under active investigation",
  },
  {
    value: "Resolved",
    label: "Resolved",
    tone: "emerald",
    description: "Decision reached",
  },
  {
    value: "Closed",
    label: "Closed",
    tone: "zinc",
    description: "Concluded and archived",
  },
];

const toneClasses = {
  slate: "bg-slate-100 text-slate-700 ring-slate-200",
  amber: "bg-amber-50 text-amber-800 ring-amber-200",
  emerald: "bg-emerald-50 text-emerald-800 ring-emerald-200",
  zinc: "bg-zinc-100 text-zinc-700 ring-zinc-200",
};

const statusMeta = (status) =>
  STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];

const formatDate = (value, withTime = true) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    ...(withTime
      ? { hour: "2-digit", minute: "2-digit" }
      : {}),
  }).format(date);
};

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const StatusBadge = ({ status }) => {
  const meta = statusMeta(status);
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${
        toneClasses[meta.tone]
      }`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {meta.label}
    </span>
  );
};

const CasesManager = () => {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await casesApi.getAdminCases({
        page: 1,
        pageSize: 100,
        status: statusFilter || undefined,
      });
      setItems(data?.items || []);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (c) =>
        c.caseId?.toLowerCase().includes(q) ||
        c.subject?.toLowerCase().includes(q) ||
        c.fullName?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.category?.toLowerCase().includes(q) ||
        c.institution?.toLowerCase().includes(q)
    );
  }, [items, query]);

  const stats = useMemo(() => {
    const counts = Object.fromEntries(
      STATUS_OPTIONS.map((s) => [s.value, 0])
    );
    items.forEach((item) => {
      if (counts[item.status] != null) counts[item.status] += 1;
    });
    return counts;
  }, [items]);

  const openDetail = async (id) => {
    setSelectedId(id);
    setDetailLoading(true);
    try {
      const data = await casesApi.getAdminCaseById(id);
      setDetail(data);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedId(null);
    setDetail(null);
  };

  const handleStatusChange = async (nextStatus) => {
    if (!detail || detail.status === nextStatus) return;
    setBusy(true);
    try {
      const updated = await casesApi.updateCaseStatus(detail.id, {
        status: nextStatus,
        rowVersion: detail.rowVersion,
      });
      setDetail(updated);
      toast.success("Case status updated.");
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await casesApi.deleteCase(deleting.id);
      toast.success("Case deleted.");
      if (selectedId === deleting.id) closeDetail();
      setDeleting(null);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardLayout
      title="Cases"
      subtitle="Investigate submitted reports, update status, and manage case records."
    >
      <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {STATUS_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() =>
              setStatusFilter((current) =>
                current === option.value ? "" : option.value
              )
            }
            className={[
              "rounded-2xl border bg-white p-4 text-left shadow-sm transition",
              statusFilter === option.value
                ? "border-slate-900 ring-2 ring-slate-900/10"
                : "border-slate-200 hover:border-slate-300",
            ].join(" ")}
          >
            <div className="flex items-center justify-between gap-3">
              <StatusBadge status={option.value} />
              <span className="text-2xl font-semibold tabular-nums text-slate-900">
                {stats[option.value] || 0}
              </span>
            </div>
            <p className="mt-2 text-xs text-slate-500">{option.description}</p>
          </button>
        ))}
      </div>

      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-1 flex-col gap-3 sm:flex-row">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search Case ID, subject, reporter, institution…"
            className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10"
          />
          {statusFilter ? (
            <button
              type="button"
              onClick={() => setStatusFilter("")}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              <HiOutlineXMark className="h-4 w-4" />
              Clear filter
            </button>
          ) : null}
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          <HiOutlineArrowPath className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {loading ? (
        <LoadingState label="Loading cases…" />
      ) : error ? (
        <ErrorState message={error} />
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No cases found"
          description={
            query || statusFilter
              ? "Try adjusting your search or status filter."
              : "Submitted reports will appear here with a unique Case ID."
          }
        />
      ) : (
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <p className="text-sm font-semibold text-slate-900">
                {filtered.length} case{filtered.length === 1 ? "" : "s"}
              </p>
              <p className="text-xs text-slate-500">Click a row to open details</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100 text-left text-sm">
                <thead className="bg-slate-50/80 text-[11px] uppercase tracking-[0.14em] text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Case</th>
                    <th className="px-4 py-3 font-semibold">Report</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold">Submitted</th>
                    <th className="px-4 py-3 font-semibold">Files</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((item) => {
                    const active = selectedId === item.id;
                    return (
                      <tr
                        key={item.id}
                        onClick={() => openDetail(item.id)}
                        className={[
                          "cursor-pointer transition",
                          active
                            ? "bg-slate-900 text-white"
                            : "hover:bg-slate-50",
                        ].join(" ")}
                      >
                        <td className="px-4 py-3.5 align-top">
                          <p
                            className={[
                              "font-mono text-xs font-semibold",
                              active ? "text-white" : "text-slate-900",
                            ].join(" ")}
                          >
                            {item.caseId}
                          </p>
                          <p
                            className={[
                              "mt-1 text-xs",
                              active ? "text-slate-300" : "text-slate-500",
                            ].join(" ")}
                          >
                            {item.fullName}
                          </p>
                        </td>
                        <td className="px-4 py-3.5 align-top">
                          <p
                            className={[
                              "line-clamp-1 font-medium",
                              active ? "text-white" : "text-slate-800",
                            ].join(" ")}
                          >
                            {item.subject}
                          </p>
                          <p
                            className={[
                              "mt-1 line-clamp-1 text-xs",
                              active ? "text-slate-300" : "text-slate-500",
                            ].join(" ")}
                          >
                            {item.category} · {item.institution}
                          </p>
                        </td>
                        <td className="px-4 py-3.5 align-top">
                          {active ? (
                            <span className="inline-flex rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold text-white ring-1 ring-inset ring-white/20">
                              {statusMeta(item.status).label}
                            </span>
                          ) : (
                            <StatusBadge status={item.status} />
                          )}
                        </td>
                        <td
                          className={[
                            "px-4 py-3.5 align-top text-xs",
                            active ? "text-slate-300" : "text-slate-600",
                          ].join(" ")}
                        >
                          {formatDate(item.submittedAtUtc)}
                        </td>
                        <td
                          className={[
                            "px-4 py-3.5 align-top text-xs tabular-nums",
                            active ? "text-slate-300" : "text-slate-600",
                          ].join(" ")}
                        >
                          {item.attachmentCount || 0}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            {!selectedId ? (
              <div className="flex min-h-[420px] flex-col items-center justify-center gap-3 px-6 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
                  <HiOutlineDocumentText className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    Select a case
                  </p>
                  <p className="mt-1 max-w-xs text-sm text-slate-500">
                    Open a report to review evidence, update status, or delete
                    the record.
                  </p>
                </div>
              </div>
            ) : detailLoading ? (
              <div className="p-5">
                <LoadingState label="Loading case…" />
              </div>
            ) : !detail ? (
              <div className="p-5">
                <ErrorState message="Could not load case details." />
              </div>
            ) : (
              <div className="flex max-h-[calc(100vh-220px)] flex-col">
                <div className="border-b border-slate-100 px-5 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-700">
                        Case dossier
                      </p>
                      <h3 className="mt-1 font-mono text-lg font-semibold text-slate-900">
                        {detail.caseId}
                      </h3>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <StatusBadge status={detail.status} />
                        <span className="text-xs text-slate-500">
                          Updated {formatDate(detail.updatedAtUtc)}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={closeDetail}
                      className="rounded-xl border border-slate-200 p-2 text-slate-500 hover:bg-slate-50"
                      aria-label="Close details"
                    >
                      <HiOutlineXMark className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
                  <section>
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                      Workflow status
                    </p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {STATUS_OPTIONS.map((option) => {
                        const active = detail.status === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            disabled={busy || active}
                            onClick={() => handleStatusChange(option.value)}
                            className={[
                              "rounded-2xl border px-3 py-3 text-left transition",
                              active
                                ? "border-slate-900 bg-slate-900 text-white"
                                : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50",
                              busy ? "opacity-60" : "",
                            ].join(" ")}
                          >
                            <p className="text-sm font-semibold">{option.label}</p>
                            <p
                              className={[
                                "mt-0.5 text-xs",
                                active ? "text-slate-300" : "text-slate-500",
                              ].join(" ")}
                            >
                              {option.description}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </section>

                  <section className="rounded-2xl border border-slate-100 bg-slate-50/80 p-4">
                    <div className="flex items-start gap-2">
                      <HiOutlineDocumentText className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900">
                          {detail.subject}
                        </h4>
                        <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-600">
                          {detail.description}
                        </p>
                      </div>
                    </div>
                  </section>

                  <section>
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                      Incident details
                    </p>
                    <dl className="grid gap-2 sm:grid-cols-2">
                      {[
                        ["Category", detail.category],
                        ["Institution", detail.institution],
                        ["Location", detail.location],
                        ["Incident date", detail.incidentDate || "—"],
                        ["Submitted", formatDate(detail.submittedAtUtc)],
                      ].map(([label, value]) => (
                        <div
                          key={label}
                          className="rounded-xl border border-slate-100 bg-white px-3 py-2.5"
                        >
                          <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                            {label}
                          </dt>
                          <dd className="mt-1 text-sm text-slate-800">{value}</dd>
                        </div>
                      ))}
                    </dl>
                  </section>

                  <section>
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                      Reporter (confidential)
                    </p>
                    <dl className="grid gap-2 sm:grid-cols-2">
                      {[
                        ["Full name", detail.fullName],
                        ["National ID", detail.nationalId],
                        ["Email", detail.email],
                        ["Phone", detail.phone || "—"],
                      ].map(([label, value]) => (
                        <div
                          key={label}
                          className="rounded-xl border border-slate-100 bg-white px-3 py-2.5"
                        >
                          <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                            {label}
                          </dt>
                          <dd className="mt-1 break-all text-sm text-slate-800">
                            {value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </section>

                  <section>
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                      Attachments
                    </p>
                    {detail.attachments?.length > 0 ? (
                      <ul className="space-y-2">
                        {detail.attachments.map((file) => (
                          <li key={file.id}>
                            <a
                              href={resolveMediaUrl(file.url)}
                              target="_blank"
                              rel="noreferrer"
                              className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 transition hover:border-emerald-300 hover:bg-emerald-50/50"
                            >
                              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                                <HiOutlinePaperClip className="h-4 w-4" />
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block truncate font-medium">
                                  {file.originalFileName}
                                </span>
                                <span className="text-xs text-slate-500">
                                  {formatBytes(file.sizeBytes)}
                                </span>
                              </span>
                            </a>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="rounded-xl border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-500">
                        No files were attached to this report.
                      </p>
                    )}
                  </section>
                </div>

                <div className="border-t border-slate-100 px-5 py-4">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setDeleting(detail)}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-semibold text-rose-700 transition hover:bg-rose-100 disabled:opacity-60"
                  >
                    <HiOutlineTrash className="h-4 w-4" />
                    Delete case
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete this case?"
        description={
          deleting
            ? `This permanently removes ${deleting.caseId} and its attachments. This cannot be undone.`
            : ""
        }
        confirmLabel="Delete case"
        busy={busy}
        onCancel={() => setDeleting(null)}
        onConfirm={handleDelete}
      />
    </DashboardLayout>
  );
};

export default CasesManager;
